import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getReceiptsByFileOpening } from '../services/receipts';
import toast from 'react-hot-toast';
import { deleteReceipt } from '../services/receipts';
import ConfirmationModal from '../components/ConfirmationModal';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const ReceiptList = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReceiptId, setSelectedReceiptId] = useState(null);

    useEffect(() => {
        const fetchReceipts = async () => {
            if (!id) return;

            try {
                const offset = (currentPage - 1) * limit;
                const { receipts, count } = await getReceiptsByFileOpening(id, {
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    searchTerm
                });
                setReceipts(receipts);
                setTotal(count);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchReceipts();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [id, limit, currentPage, sortBy, sortOrder, searchTerm]);

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSortByChange = (e) => {
        const [newSortBy, newSortOrder] = e.target.value.split(':');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const openModal = (receiptId) => {
        setSelectedReceiptId(receiptId);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedReceiptId(null);
        setIsModalOpen(false);
    };

    const handleDelete = async () => {
        if (selectedReceiptId) {
            try {
                await deleteReceipt(selectedReceiptId);
                toast.success('Receipt deleted successfully!');
                // Refetch the data to update the list
                const offset = (currentPage - 1) * limit;
                const { receipts, count } = await getReceiptsByFileOpening(id, {
                    limit,
                    offset,
                    sortBy,
                    sortOrder,
                    searchTerm
                });
                setReceipts(receipts);
                setTotal(count);
            } catch (error) {
                toast.error(error.message);
            }
            closeModal();
        }
    };

    const getPaymentMethodBadge = (method) => {
        const methodConfig = {
            'cash': { class: 'bg-green-100 text-green-800', label: 'Cash' },
            'card': { class: 'bg-blue-100 text-blue-800', label: 'Card' },
            'bank': { class: 'bg-purple-100 text-purple-800', label: 'Bank Transfer' },
            'other': { class: 'bg-gray-100 text-gray-800', label: 'Other' }
        };

        const config = methodConfig[method] || { class: 'bg-gray-100 text-gray-800', label: method };

        return (
            <span className={`px-2 py-1 rounded-full text-xs ${config.class}`}>
                {config.label}
            </span>
        );
    };

    const getPaymentStatusBadge = (paymentReceived) => {
        console.log('Payment Received:', paymentReceived);
        return paymentReceived ? (
            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-900">
                Received
            </span>
        ) : (
            <span className="px-2 py-1 rounded-full text-xs bg-orange-300 text-orange-900">
                Paid
            </span>
        );
    };

    const getBalanceBadge = (balanceAmount) => {
        return balanceAmount > 0 ? (
            <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                £{balanceAmount?.toFixed(2)}
            </span>
        ) : (
            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                £{Math.abs(balanceAmount || 0)?.toFixed(2)}
            </span>
        );
    };

    return (
        <DashboardLayout pageTitle={`Receipts - File Opening #${id}`}>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                    <input
                        type="text"
                        placeholder="Search receipts..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    />
                    <select
                        value={limit}
                        onChange={handleLimitChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <select
                        value={`${sortBy}:${sortOrder}`}
                        onChange={handleSortByChange}
                        className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2"
                    >
                        <option value="id:desc">ID (Z-A)</option>
                        <option value="id:asc">ID (A-Z)</option>
                        <option value="date:desc">Date (Newest First)</option>
                        <option value="date:asc">Date (Oldest First)</option>
                        <option value="amount:desc">Amount (Highest First)</option>
                        <option value="amount:asc">Amount (Lowest First)</option>
                        <option value="balance_amount:desc">Balance (Highest First)</option>
                        <option value="balance_amount:asc">Balance (Lowest First)</option>
                        <option value="payment_method:asc">Payment Method (A-Z)</option>
                        <option value="payment_method:desc">Payment Method (Z-A)</option>
                    </select>
                </div>
                <div>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                    >
                        Back
                    </button>
                    <Link
                        to={`/file-opening/${id}/receipt/create`}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow"
                    >
                        New Receipt
                    </Link>
                </div>
            </div>
            <TableContainer>
                {loading ? (
                    <SimpleLoader />
                ) : (
                    <>
                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Receipt #</th>
                                    <th scope="col" className="px-6 py-3">Amount</th>
                                    <th scope="col" className="px-6 py-3">Balance</th>
                                    <th scope="col" className="px-6 py-3">Payment Method</th>
                                    <th scope="col" className="px-6 py-3">Date</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    {/* <th scope="col" className="px-6 py-3">Reference</th> */}
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.length > 0 ? (
                                    receipts.map((receipt) => (
                                        <tr key={receipt.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                                {receipt.id}
                                            </td>
                                            <td className="px-6 py-4 font-semibold">
                                                £{receipt.amount?.toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {getBalanceBadge(receipt.balance_amount)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {getPaymentMethodBadge(receipt.payment_method)}
                                            </td>
                                            <td className="px-6 py-4">{receipt.date}</td>
                                            <td className="px-6 py-4">
                                                {getPaymentStatusBadge(receipt.payment_received)}
                                            </td>
                                            {/* <td className="px-6 py-4">
                                                {receipt.reference_number || 'N/A'}
                                            </td> */}
                                            <td className="px-6 py-4">
                                                <Link
                                                    to={`/file-opening/${id}/receipt/${receipt.id}`}
                                                    className="text-blue-600 hover:underline mr-4"
                                                >
                                                    View
                                                </Link>
                                                <Link
                                                    to={`/file-opening/${id}/receipt/${receipt.id}/edit`}
                                                    className="text-blue-600 hover:underline mr-4"
                                                >
                                                    Edit
                                                </Link>
                                                <button
                                                    onClick={() => openModal(receipt.id)}
                                                    className="text-red-600 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                            No receipts found for this file opening.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination
                            currentPage={currentPage}
                            total={total}
                            limit={limit}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </TableContainer>
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this receipt? This action cannot be undone."
            />
        </DashboardLayout>
    );
};

export default ReceiptList;